import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarClock, Check, Download, Loader2, Mail, TrendingDown, TrendingUp } from "lucide-react";
import Layout from "../components/Layout";
import { api, API_BASE, currency, multiple, percent } from "../lib/api";

const SCORE_COLOR = (s) => {
  if (s >= 90) return "text-emerald-600";
  if (s >= 82) return "text-orange-500";
  if (s >= 75) return "text-amber-600";
  return "text-rose-600";
};

export default function Results() {
  const { id } = useParams();
  const location = useLocation();
  const [data, setData] = useState(location.state?.data || null);
  const [loading, setLoading] = useState(!location.state?.data && !!id);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (data || !id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/valuation/${id}`)
      .then((res) => { if (!cancelled) { setData(res.data); setError(null); } })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.status === 404 ? "not_found" : "error");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, data]);

  if (loading) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-20 text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-orange-500" />
          <p className="mt-4 text-sm text-slate-500">Loading your valuation…</p>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-20 text-center">
          <h2 className="font-display text-2xl font-bold text-slate-900">
            {error === "not_found" ? "Valuation not found" : "No result available."}
          </h2>
          <p className="mt-4 text-slate-600">
            {error === "not_found"
              ? "The valuation you're trying to view no longer exists or the link is invalid."
              : "Please start a new valuation from the home page."}
          </p>
          <Link to="/valuation" className="btn-primary mt-8 inline-flex" data-testid="results-empty-start-btn">
            Start valuation
          </Link>
        </div>
      </Layout>
    );
  }

  const { result, benchmarks, disclaimer, email_status } = data;
  const positive = result.positive_factors || [];
  const negative = result.negative_factors || [];

  const pdfUrl = `${API_BASE}/valuation/${id}/pdf`;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-6 py-10 sm:py-16">
        {!result.valuable_by_earnings ? (
          <NoEarningsResult result={result} />
        ) : (
          <>
            {/* Hero valuation */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:p-12"
            >
              <div className="overline">Estimated market value</div>
              <div className="mt-3 flex flex-wrap items-baseline gap-3 font-display text-slate-900" data-testid="result-range">
                <span className="text-4xl font-extrabold sm:text-6xl">{currency(result.low_value)}</span>
                <span className="text-2xl text-slate-400 sm:text-4xl">–</span>
                <span className="text-4xl font-extrabold text-orange-500 sm:text-6xl">{currency(result.high_value)}</span>
              </div>
              <div className="mt-3 text-sm text-slate-600">
                Central estimate <span className="font-semibold text-slate-900" data-testid="result-central">{currency(result.central_value)}</span>
                {" · "}Applied multiple {multiple(result.adjusted_multiple_low)} – {multiple(result.adjusted_multiple_high)}
                {result.revenue_multiple != null && <> · Revenue multiple {multiple(result.revenue_multiple)}</>}
              </div>

              {result.selected_method && (
                <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700" data-testid="selected-method">
                  Selected method:&nbsp;
                  <span className="text-orange-600">
                    {result.selected_method === "equipment_backed"
                      ? "Equipment + 1yr earnings (higher of the two)"
                      : "SDE multiple (higher of the two)"}
                  </span>
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <a
                  href="https://calendly.com/bonza-business-and-franchise-sales/sell-my-gym"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="book-call-btn"
                  onClick={() => {
                    if (typeof window !== "undefined" && typeof window.gtag === "function") {
                      window.gtag("event", "book_call_clicked", { source: "results_page" });
                    }
                  }}
                  className="btn-primary"
                >
                  <CalendarClock className="h-4 w-4" /> Book a call with a broker
                </a>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="download-pdf-btn"
                  className="btn-ghost"
                >
                  <Download className="h-4 w-4" /> Download PDF report
                </a>
                <a
                  href="mailto:sales@bonzabfs.com.au?subject=Discuss my gym valuation"
                  data-testid="discuss-btn"
                  className="btn-ghost"
                >
                  Discuss My Valuation <ArrowRight className="h-4 w-4" />
                </a>
                {email_status?.status === "sent" && (
                  <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    <Mail className="h-3.5 w-3.5" /> Report emailed
                  </span>
                )}
              </div>

              {result.warnings?.length > 0 && (
                <div className="mt-6 space-y-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                  {result.warnings.map((w, i) => <div key={i}>• {w}</div>)}
                </div>
              )}
            </motion.div>

            {/* Two-method comparison */}
            {(result.equipment?.original_cost > 0 || result.earnings_valuation_central != null) && (
              <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <MethodCard
                  active={result.selected_method === "earnings_multiple"}
                  title="Method 1 · PEBITDA multiple"
                  headline={currency(result.earnings_valuation_central)}
                  subline={`${currency(result.earnings_valuation_low)} – ${currency(result.earnings_valuation_high)}`}
                  rows={[
                    ["Normalised PEBITDA", currency(result.normalised_pebitda)],
                    ["Applied multiple",
                      `${multiple(result.adjusted_multiple_low)} – ${multiple(result.adjusted_multiple_high)}`],
                  ]}
                />
                <MethodCard
                  active={result.selected_method === "equipment_backed"}
                  title="Method 2 · Equipment + 1yr earnings"
                  headline={currency(result.equipment_backed_value)}
                  subline={
                    result.equipment?.original_cost > 0
                      ? "Adjusted equipment value + one year of normalised earnings"
                      : "No equipment value provided"
                  }
                  rows={[
                    ["Original equipment cost", currency(result.equipment?.original_cost)],
                    ["Age-based value (reducing-balance)", currency(result.equipment?.age_based_value)],
                    ["Adjusted equipment value (post-condition)", currency(result.equipment?.adjusted_value)],
                    ["One year of normalised earnings", currency(Math.max(0, result.normalised_pebitda || 0))],
                  ]}
                />
              </div>
            )}

            {/* Equipment breakdown */}
            {result.equipment?.breakdown?.length > 0 && (
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
                <div className="overline">Equipment breakdown</div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="py-2">Level</th>
                        <th className="py-2">Original cost</th>
                        <th className="py-2">Age (yrs)</th>
                        <th className="py-2">Dep. rate</th>
                        <th className="py-2">Age-based value</th>
                        <th className="py-2">Condition adj.</th>
                        <th className="py-2">Adjusted value</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.equipment.breakdown.map((b, i) => (
                        <tr key={i}>
                          <td className="py-2 font-medium text-slate-800">{b.level_label}</td>
                          <td className="py-2 text-slate-700">{currency(b.original_cost)}</td>
                          <td className="py-2 text-slate-700">{b.age_years?.toFixed(1)}</td>
                          <td className="py-2 text-slate-700">{(b.depreciation_rate * 100).toFixed(2)}%</td>
                          <td className="py-2 text-slate-700">{currency(b.age_based_value)}</td>
                          <td className="py-2 text-slate-700">
                            {b.condition_adjustment > 0 ? "+" : ""}{(b.condition_adjustment * 100).toFixed(0)}%
                          </td>
                          <td className="py-2 font-semibold text-slate-900">{currency(b.adjusted_value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  This valuation assumes any finance, lease liability or security
                  interest affecting the equipment will be paid out by the vendor
                  at settlement. The purchaser is assumed to receive clear title
                  to all equipment included in the sale.
                </p>
              </div>
            )}

            {/* Metrics grid */}
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              <Metric label="Normalised SDE" value={currency(result.normalised_pebitda)} />
              <Metric label="Working-owner SDE" value={currency(result.working_owner_pebitda)} />
              <Metric label="SDE margin" value={percent(result.operating_metrics?.pebitda_margin)} />
              <Metric label="Revenue / member" value={currency(result.operating_metrics?.revenue_per_member)} />
              <Metric label="Member growth" value={percent(result.operating_metrics?.member_growth_rate)} />
              <Metric
                label="Members / employee"
                value={result.operating_metrics?.members_per_employee == null
                  ? "Owner-operated"
                  : result.operating_metrics.members_per_employee.toFixed(0)}
              />
            </div>

            <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Saleability score */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="overline">Saleability</div>
                <div className={`mt-3 font-display text-5xl font-extrabold ${SCORE_COLOR(result.saleability_score)}`} data-testid="saleability-score">
                  {result.saleability_score}<span className="text-2xl text-slate-400">/100</span>
                </div>
                <div className="mt-2 text-sm font-semibold text-slate-800">{result.saleability_band}</div>
                <p className="mt-3 text-xs text-slate-500">
                  Reflects growth, owner independence, management structure, financial quality, lease and equipment.
                </p>
              </div>

              {/* Positive factors */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                  <TrendingUp className="h-4 w-4" /> Factors supporting the valuation
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  {positive.length === 0 ? <li className="text-slate-500">No strong positive drivers identified.</li> :
                    positive.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> {p}
                      </li>
                    ))
                  }
                </ul>
              </div>

              {/* Negative factors */}
              <div className="rounded-2xl border border-slate-200 bg-white p-6">
                <div className="mb-3 inline-flex items-center gap-2 text-sm font-semibold text-rose-700">
                  <TrendingDown className="h-4 w-4" /> Factors reducing the valuation
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  {negative.length === 0 ? <li className="text-slate-500">No significant risk factors identified.</li> :
                    negative.map((p, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" /> {p}
                      </li>
                    ))
                  }
                </ul>
              </div>
            </div>

            {/* Benchmarks */}
            {benchmarks && !benchmarks.limited && Object.keys(benchmarks.values || {}).length > 0 && (
              <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
                <div className="overline">Benchmark comparison — {benchmarks.gym_type_label}</div>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="py-2">Metric</th>
                        <th className="py-2">Your gym</th>
                        <th className="py-2">Benchmark</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      <BenchRow label="Revenue per member"
                        yours={currency(result.operating_metrics?.revenue_per_member)}
                        bench={currency(benchmarks.values.revenue_per_member)} />
                      <BenchRow label="SDE margin"
                        yours={percent(result.operating_metrics?.pebitda_margin)}
                        bench={percent(benchmarks.values.pebitda_margin)} />
                      <BenchRow label="Members per employee"
                        yours={result.operating_metrics?.members_per_employee == null ? "Owner-operated" : result.operating_metrics.members_per_employee.toFixed(0)}
                        bench={benchmarks.values.members_per_employee?.toFixed(0) || "—"} />
                      <BenchRow label="Annual member growth"
                        yours={percent(result.operating_metrics?.member_growth_rate)}
                        bench={percent(benchmarks.values.member_growth_rate)} />
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {benchmarks?.limited && (
              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                Limited comparable data is currently available for this gym type.
              </div>
            )}

            {/* Explanation */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
              <div className="overline">Result explanation</div>
              <p className="mt-3 text-sm leading-relaxed text-slate-700" data-testid="explanation">{result.explanation}</p>
            </div>

            {/* Disclaimer */}
            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-xs leading-relaxed text-slate-500">
              <div className="overline mb-2">Disclaimer</div>
              {disclaimer}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-sm">
      <div className="overline">{label}</div>
      <div className="mt-2 font-display text-xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function MethodCard({ title, headline, subline, rows, active }) {
  return (
    <div
      className={`rounded-2xl border p-6 transition-colors ${
        active
          ? "border-orange-300 bg-orange-50/50 ring-1 ring-orange-200"
          : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="overline">{title}</div>
        {active && (
          <span className="rounded-full bg-orange-500 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            Selected
          </span>
        )}
      </div>
      <div className="mt-3 font-display text-3xl font-extrabold text-slate-900">{headline}</div>
      <div className="mt-1 text-xs text-slate-600">{subline}</div>
      <dl className="mt-4 space-y-2 border-t border-slate-100 pt-4 text-sm">
        {rows.map(([label, value], i) => (
          <div key={i} className="flex items-center justify-between">
            <dt className="text-slate-600">{label}</dt>
            <dd className="font-semibold text-slate-900">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function BenchRow({ label, yours, bench }) {
  return (
    <tr>
      <td className="py-2 text-slate-600">{label}</td>
      <td className="py-2 font-semibold text-slate-900">{yours}</td>
      <td className="py-2 text-slate-700">{bench}</td>
    </tr>
  );
}

function NoEarningsResult({ result }) {
  const hasEquipment = result?.equipment?.original_cost > 0 && result?.selected_method === "equipment_backed";
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-8 sm:p-12">
        <div className="overline text-amber-700">Earnings-based valuation not applicable</div>
        <h2 className="mt-3 font-display text-3xl font-bold text-slate-900">
          {hasEquipment
            ? "Value is supported primarily by the equipment included in the sale."
            : "Your business does not currently produce sufficient maintainable earnings."}
        </h2>
        <p className="mt-4 max-w-2xl text-slate-700">{result?.message}</p>
        {hasEquipment && (
          <div className="mt-6">
            <div className="overline">Indicative equipment-backed value</div>
            <div className="mt-2 flex flex-wrap items-baseline gap-3 font-display text-slate-900" data-testid="result-range">
              <span className="text-3xl font-extrabold sm:text-5xl">{currency(result.low_value)}</span>
              <span className="text-2xl text-slate-400">–</span>
              <span className="text-3xl font-extrabold text-orange-500 sm:text-5xl">{currency(result.high_value)}</span>
            </div>
            <div className="mt-2 text-sm text-slate-700">
              Central estimate <span className="font-semibold" data-testid="result-central">{currency(result.central_value)}</span>
            </div>
          </div>
        )}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href="https://calendly.com/bonza-business-and-franchise-sales/sell-my-gym"
            target="_blank"
            rel="noreferrer"
            data-testid="book-call-btn"
            className="btn-primary"
          >
            Book a call with a broker <ArrowRight className="h-4 w-4" />
          </a>
          <a href="mailto:sales@bonzabfs.com.au" className="btn-ghost">
            Discuss with a specialist <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>

      {hasEquipment && result?.equipment?.breakdown?.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="overline">Equipment breakdown</div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="py-2">Level</th>
                  <th className="py-2">Original cost</th>
                  <th className="py-2">Age (yrs)</th>
                  <th className="py-2">Age-based value</th>
                  <th className="py-2">Condition adj.</th>
                  <th className="py-2">Adjusted value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {result.equipment.breakdown.map((b, i) => (
                  <tr key={i}>
                    <td className="py-2 font-medium text-slate-800">{b.level_label}</td>
                    <td className="py-2">{currency(b.original_cost)}</td>
                    <td className="py-2">{b.age_years?.toFixed(1)}</td>
                    <td className="py-2">{currency(b.age_based_value)}</td>
                    <td className="py-2">
                      {b.condition_adjustment > 0 ? "+" : ""}{(b.condition_adjustment * 100).toFixed(0)}%
                    </td>
                    <td className="py-2 font-semibold text-slate-900">{currency(b.adjusted_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            The valuation assumes any finance, lease liability or security interest affecting the equipment
            will be paid out by the vendor at settlement.
          </p>
        </div>
      )}
    </div>
  );
}
